import { useCallback, useState } from "react";
import { ZodError } from "zod";
import {
  ChannelFull,
  CMInfoUpdateSectionType,
  CustomerInfo,
  UpdateCustomerInfoRequest,
  ValidationErrors,
} from "../../lib/api/models";
import {
  CMINFO_OTHER_LANGUAGE_PREFERENCE_ID,
  cmInfoBillingAddressRequestSchema,
  cmInfoContactPreferenceRequestSchema,
  cmInfoEmailRequestSchema,
  cmInfoEmploymentIncomeRequestSchema,
  cmInfoLanguageRequestSchema,
  cmInfoP2DRequestSchema,
  cmInfoRequestSchema,
  cmInfoSchoolRequestSchema,
  cmInfoSecurityRequestSchema,
} from "../../lib/validation/schemas";
import { useUpdateCustomerInfo } from "../../lib/api/queries";
import { setValidationErrorsAndDisplayGeneralErrors } from "../../lib/validation";
import {
  convertDateStringToMonthDayYearFormat,
  formatToDateStringInCentralTime,
} from "../../lib/formatting";

export const enum CMInfoSubtool {
  Language = "language",
  BillingAddress = "billingAddress",
  Email = "email",
  P2D = "p2D",
  EmploymentIncome = "employmentIncome",
  School = "school",
  ContactPreference = "contactPreference",
  Security = "security",
}

const schemas = {
  [CMInfoSubtool.Language]: cmInfoLanguageRequestSchema,
  [CMInfoSubtool.BillingAddress]: cmInfoBillingAddressRequestSchema,
  [CMInfoSubtool.Email]: cmInfoEmailRequestSchema,
  [CMInfoSubtool.P2D]: cmInfoP2DRequestSchema,
  [CMInfoSubtool.EmploymentIncome]: cmInfoEmploymentIncomeRequestSchema,
  [CMInfoSubtool.School]: cmInfoSchoolRequestSchema,
  [CMInfoSubtool.ContactPreference]: cmInfoContactPreferenceRequestSchema,
  [CMInfoSubtool.Security]: cmInfoSecurityRequestSchema,
};

export type CMInfoFormInput = {
  [CMInfoSubtool.Language]: {
    languagePreferenceId: string;
    otherLanguage: string;
    languageLastVerifiedDate: string | null;
    languageDueForVerification: boolean;
    isLanguageVerified: boolean;
  };
  [CMInfoSubtool.BillingAddress]: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zip: string;
    billingAddressLastVerifiedDate: string | null;
    billingAddressDueForVerification: boolean;
    isBillingAddressVerified: boolean;
  };
  [CMInfoSubtool.Email]: {
    primaryEmail: string;
    primaryEmailLastVerifiedDate: string | null;
    primaryEmailDueForVerification: boolean;
    isPrimaryEmailVerified: boolean;
    alternateEmail: string;
    alternateEmailLastVerifiedDate: string | null;
    alternateEmailDueForVerification: boolean;
    isAlternateEmailVerified: boolean;
    emailOptOut: boolean;
    emailOptOutLastVerifiedDate: string | null;
    emailOptOutDueForVerification: boolean;
    isEmailOptOutVerified: boolean;
    isPaperless: boolean;
  };
  [CMInfoSubtool.P2D]: {
    permissionToDiscussLastVerifiedDate: string | null;
    permissionToDiscussDueForVerification: boolean;
    isPermissionToDiscussVerified: boolean;
  };
  [CMInfoSubtool.EmploymentIncome]: {
    source: string;
    incomeLastVerifiedDate: string | null;
    incomeDueForVerification: boolean;
    isIncomeVerified: boolean;
  };
  [CMInfoSubtool.School]: {
    school: string;
    graduationDate: string;
    graduationStatus: "grad" | "NA" | null;
    schoolLastVerifiedDate: string | null;
    schoolDueForVerification: boolean;
    isSchoolVerified: boolean;
  };
  [CMInfoSubtool.ContactPreference]: {
    contactPreference: Record<string, number | null>;
    contactPreferenceLastVerifiedDate: string | null;
    contactPreferenceDueForVerification: boolean;
    isContactPreferenceVerified: boolean;
  };
  [CMInfoSubtool.Security]: {
    dateOfBirthLastVerifiedDate: string | null;
    dateOfBirthDueForVerification: boolean;
    isDateOfBirthVerified: boolean;
    mothersMaidenNameLastVerifiedDate: string | null;
    mothersMaidenNameDueForVerification: boolean;
    isMothersMaidenNameVerified: boolean;
    securityLastVerifiedDate: string | null;
    securityDueForVerification: boolean;
    isSecurityVerified: boolean;
    ssnLast4LastVerifiedDate: string | null;
    ssnLast4DueForVerification: boolean;
    isSsnLast4Verified: boolean;
  };
};

// For CM Info, we lock the `is__Verified` fields to `true` anytime edits are made (even if they're undone manually).
// This means that we can determine if a section is dirty just by referencing the `is__Verified` fields, however,
// that would require listing out those checks manually since there's no easy way to check those 'automatically'.
// Instead, we can just perform a 'naive' check of all current values vs their initial values since this would
// include those `is__Verified` fields.
export const isFormDirty = (
  formState: CMInfoFormInput,
  initialFormState: CMInfoFormInput,
) =>
  (Object.keys(formState) as Array<CMInfoSubtool>).some((section) =>
    (
      Object.keys(formState[section]) as Array<
        keyof (typeof formState)[typeof section]
      >
    ).some((key) => formState[section][key] !== initialFormState[section][key]),
  );

const getInitialFormState = (customerInfo: CustomerInfo): CMInfoFormInput => {
  return {
    [CMInfoSubtool.Language]: {
      languagePreferenceId: customerInfo.language.languagePreferenceId,
      otherLanguage: customerInfo.language.otherLanguage ?? "",
      languageLastVerifiedDate: customerInfo.languageVerifiedStatus.date,
      languageDueForVerification:
        customerInfo.languageVerifiedStatus.dueForVerification,
      isLanguageVerified: false,
    },
    [CMInfoSubtool.BillingAddress]: {
      addressLine1: customerInfo.billingAddress.addressLine1,
      addressLine2: customerInfo.billingAddress.addressLine2,
      city: customerInfo.billingAddress.city,
      state: customerInfo.billingAddress.state,
      zip: customerInfo.billingAddress.zip,
      billingAddressLastVerifiedDate:
        customerInfo.billingAddressVerifiedStatus.date,
      billingAddressDueForVerification:
        customerInfo.billingAddressVerifiedStatus.dueForVerification,
      isBillingAddressVerified: false,
    },
    [CMInfoSubtool.Email]: {
      primaryEmail: customerInfo.email.primaryEmail ?? "",
      primaryEmailLastVerifiedDate:
        customerInfo.primaryEmailVerifiedStatus.date,
      primaryEmailDueForVerification:
        customerInfo.primaryEmailVerifiedStatus.dueForVerification,
      isPrimaryEmailVerified: false,
      alternateEmail: customerInfo.email.alternateEmail ?? "",
      alternateEmailLastVerifiedDate:
        customerInfo.alternateEmailVerifiedStatus.date,
      alternateEmailDueForVerification:
        customerInfo.alternateEmailVerifiedStatus.dueForVerification,
      isAlternateEmailVerified: false,
      emailOptOut: customerInfo.email.emailOptOut ?? false,
      emailOptOutLastVerifiedDate: customerInfo.emailOptOutVerifiedStatus.date,
      emailOptOutDueForVerification:
        customerInfo.emailOptOutVerifiedStatus.dueForVerification,
      isEmailOptOutVerified: false,
      isPaperless: customerInfo.email.isPaperless ?? false,
    },
    [CMInfoSubtool.P2D]: {
      permissionToDiscussLastVerifiedDate:
        customerInfo.permissionToDiscussVerifiedStatus.date,
      permissionToDiscussDueForVerification:
        customerInfo.permissionToDiscussVerifiedStatus.dueForVerification,
      isPermissionToDiscussVerified: false,
    },
    [CMInfoSubtool.EmploymentIncome]: {
      source: customerInfo.income.source,
      incomeLastVerifiedDate: customerInfo.incomeVerifiedStatus.date,
      incomeDueForVerification:
        customerInfo.incomeVerifiedStatus.dueForVerification,
      isIncomeVerified: false,
    },
    [CMInfoSubtool.School]: {
      school: customerInfo.school.school,
      graduationDate:
        customerInfo.school.school !== "" && customerInfo.school.graduationDate
          ? customerInfo.school.graduationDate
          : "",
      graduationStatus: customerInfo.school.graduationStatus,
      schoolLastVerifiedDate: customerInfo.schoolVerifiedStatus.date,
      schoolDueForVerification:
        customerInfo.schoolVerifiedStatus.dueForVerification,
      isSchoolVerified: false,
    },
    [CMInfoSubtool.ContactPreference]: {
      contactPreference: customerInfo.contactPreference,
      contactPreferenceLastVerifiedDate:
        customerInfo.contactPreferenceVerifiedStatus.date,
      contactPreferenceDueForVerification:
        customerInfo.contactPreferenceVerifiedStatus.dueForVerification,
      isContactPreferenceVerified: false,
    },
    [CMInfoSubtool.Security]: {
      dateOfBirthLastVerifiedDate: customerInfo.dateOfBirthVerifiedStatus.date,
      dateOfBirthDueForVerification:
        customerInfo.dateOfBirthVerifiedStatus.dueForVerification,
      isDateOfBirthVerified: false,
      mothersMaidenNameLastVerifiedDate:
        customerInfo.mothersMaidenNameVerifiedStatus.date,
      mothersMaidenNameDueForVerification:
        customerInfo.mothersMaidenNameVerifiedStatus.dueForVerification,
      isMothersMaidenNameVerified: false,
      securityLastVerifiedDate: customerInfo.securityVerifiedStatus.date,
      securityDueForVerification:
        customerInfo.securityVerifiedStatus.dueForVerification,
      isSecurityVerified: false,
      ssnLast4LastVerifiedDate: customerInfo.ssnLast4VerifiedStatus.date,
      ssnLast4DueForVerification:
        customerInfo.ssnLast4VerifiedStatus.dueForVerification,
      isSsnLast4Verified: false,
    },
  };
};

const createUpdateRequest = (
  subtoolToSubmit: CMInfoSubtool | undefined,
  formState: CMInfoFormInput,
  initialFormState: CMInfoFormInput,
  dwbuid: string,
  referenceId: string,
  channel: ChannelFull,
): UpdateCustomerInfoRequest => {
  const {
    [CMInfoSubtool.Language]: { isLanguageVerified, ...language },
    [CMInfoSubtool.BillingAddress]: {
      isBillingAddressVerified,
      ...billingAddress
    },
    [CMInfoSubtool.Email]: {
      isPrimaryEmailVerified,
      isAlternateEmailVerified,
      isEmailOptOutVerified,
      ...email
    },
    [CMInfoSubtool.P2D]: { isPermissionToDiscussVerified },
    [CMInfoSubtool.EmploymentIncome]: { isIncomeVerified, ...income },
    [CMInfoSubtool.School]: { isSchoolVerified, ...school },
    [CMInfoSubtool.ContactPreference]: {
      isContactPreferenceVerified,
      contactPreference,
    },
    [CMInfoSubtool.Security]: {
      isSecurityVerified,
      isSsnLast4Verified,
      isMothersMaidenNameVerified,
      isDateOfBirthVerified,
    },
  } = formState;

  // start with blank update request and update only the fields we want to send
  const req: UpdateCustomerInfoRequest = {
    dwbuid,
    referenceId,
    channel,

    isLanguageVerified: false,
    language: null,

    isBillingAddressVerified: false,
    billingAddress: null,

    isPrimaryEmailVerified: false,
    isAlternateEmailVerified: false,
    isEmailOptOutVerified: false,
    email: null,

    isPermissionToDiscussVerified: false,

    isIncomeVerified: false,
    income: null,

    isSchoolVerified: false,
    school: null,

    isContactPreferenceVerified: false,
    contactPreference: null,

    isSsnLast4Verified: false,
    isDateOfBirthVerified: false,
    isMothersMaidenNameVerified: false,
    isSecurityVerified: false,
  };

  // handle submission of language subtool
  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.Language) &&
    isLanguageVerified
  ) {
    req.isLanguageVerified = true;
    req.language = language;

    // api expects `null` if language preference is not "Other"
    if (
      req.language.languagePreferenceId !== CMINFO_OTHER_LANGUAGE_PREFERENCE_ID
    ) {
      req.language.otherLanguage = null;
    }

    // api expects `null` if values are unchanged
    const initialLanguage = initialFormState[CMInfoSubtool.Language];
    if (
      req.language.languagePreferenceId ===
        initialLanguage.languagePreferenceId &&
      (req.language.languagePreferenceId !==
        CMINFO_OTHER_LANGUAGE_PREFERENCE_ID ||
        req.language.otherLanguage === initialLanguage.otherLanguage)
    ) {
      req.language = null;
    }
  }

  // handle submission of billing address subtool
  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.BillingAddress) &&
    isBillingAddressVerified
  ) {
    req.isBillingAddressVerified = true;
    req.billingAddress = billingAddress;

    // api expects `null` if values are unchanged
    const initialAddress = initialFormState[CMInfoSubtool.BillingAddress];
    if (
      req.billingAddress.addressLine1 === initialAddress.addressLine1 &&
      req.billingAddress.addressLine2 === initialAddress.addressLine2 &&
      req.billingAddress.city === initialAddress.city &&
      req.billingAddress.state === initialAddress.state &&
      req.billingAddress.zip === initialAddress.zip
    ) {
      req.billingAddress = null;
    }
  }

  // handle submission of email subtool
  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.Email) &&
    (isPrimaryEmailVerified ||
      isAlternateEmailVerified ||
      isEmailOptOutVerified)
  ) {
    req.isPrimaryEmailVerified = isPrimaryEmailVerified;
    req.isAlternateEmailVerified = isAlternateEmailVerified;
    req.isEmailOptOutVerified = isEmailOptOutVerified;
    req.email = email;

    // api expects `null` if values are unchanged
    const initialEmail = initialFormState[CMInfoSubtool.Email];
    if (req.email.primaryEmail === initialEmail.primaryEmail) {
      req.email.primaryEmail = null;
    }
    if (req.email.alternateEmail === initialEmail.alternateEmail) {
      req.email.alternateEmail = null;
    }
    if (req.email.emailOptOut === initialEmail.emailOptOut) {
      req.email.emailOptOut = null;
    }

    // set whole email object to `null` if all subvalues are `null`
    if (
      req.email.primaryEmail === null &&
      req.email.alternateEmail === null &&
      req.email.emailOptOut === null
    ) {
      req.email = null;
    }
  }

  // handle submission of p2d subtool
  if (subtoolToSubmit === undefined || subtoolToSubmit === CMInfoSubtool.P2D) {
    req.isPermissionToDiscussVerified = isPermissionToDiscussVerified;
  }

  // handle submission of income subtool
  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.EmploymentIncome) &&
    isIncomeVerified
  ) {
    req.isIncomeVerified = true;
    req.income = income;

    // api expects `null` if values are unchanged
    if (
      req.income.source ===
      initialFormState[CMInfoSubtool.EmploymentIncome].source
    ) {
      req.income = null;
    }
  }

  // handle submission of school subtool
  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.School) &&
    isSchoolVerified
  ) {
    req.isSchoolVerified = true;
    req.school = school;

    // api expects `null` for empty graduation date
    if (req.school.graduationDate === "") {
      req.school.graduationDate = null;
    }

    // api expects `null` if values are unchanged
    const initialSchool = initialFormState[CMInfoSubtool.School];
    if (
      req.school.school === initialSchool.school &&
      ((req.school.graduationDate === null &&
        initialSchool.graduationDate === "") ||
        req.school.graduationDate === initialSchool.graduationDate) &&
      req.school.graduationStatus === initialSchool.graduationStatus
    ) {
      req.school = null;
    }
  }

  // handle submission of contact preferences subtool
  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.ContactPreference) &&
    isContactPreferenceVerified
  ) {
    req.isContactPreferenceVerified = true;
    req.contactPreference = contactPreference;

    // api expects `null` if values are unchanged
    if (req.contactPreference !== null) {
      const initialContactPreference =
        initialFormState[CMInfoSubtool.ContactPreference];

      const isUnchanged = Object.entries(req.contactPreference).reduce(
        (agg, curr) =>
          agg &&
          curr[1] === initialContactPreference.contactPreference[curr[0]],
        true,
      );

      if (isUnchanged) {
        req.contactPreference = null;
      }
    }
  }

  // handle submission of security subtool
  if (
    subtoolToSubmit === undefined ||
    subtoolToSubmit === CMInfoSubtool.Security
  ) {
    req.isSsnLast4Verified = isSsnLast4Verified;
    req.isDateOfBirthVerified = isDateOfBirthVerified;
    req.isMothersMaidenNameVerified = isMothersMaidenNameVerified;
    req.isSecurityVerified = isSecurityVerified;
  }

  return req;
};

const getUpdatedFormState = (
  formState: CMInfoFormInput,
  subtoolToSubmit: CMInfoSubtool | undefined,
  failedSections: CMInfoUpdateSectionType[] | null,
) => {
  const today = formatToDateStringInCentralTime(new Date());
  const updatedFormState: CMInfoFormInput = { ...formState };

  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.Language) &&
    formState[CMInfoSubtool.Language].isLanguageVerified &&
    !failedSections?.includes("language")
  ) {
    updatedFormState[CMInfoSubtool.Language] = {
      ...updatedFormState[CMInfoSubtool.Language],
      languageLastVerifiedDate: today,
      languageDueForVerification: false,
      isLanguageVerified: false,
    };
  }
  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.BillingAddress) &&
    formState[CMInfoSubtool.BillingAddress].isBillingAddressVerified &&
    !failedSections?.includes("billingAddress")
  ) {
    updatedFormState[CMInfoSubtool.BillingAddress] = {
      ...updatedFormState[CMInfoSubtool.BillingAddress],
      billingAddressLastVerifiedDate: today,
      billingAddressDueForVerification: false,
      isBillingAddressVerified: false,
    };
  }
  if (
    subtoolToSubmit === undefined ||
    subtoolToSubmit === CMInfoSubtool.Email
  ) {
    updatedFormState[CMInfoSubtool.Email] = {
      ...updatedFormState[CMInfoSubtool.Email],
      ...(formState[CMInfoSubtool.Email].isPrimaryEmailVerified &&
      !failedSections?.includes("primaryEmail")
        ? {
            primaryEmailLastVerifiedDate: today,
            primaryEmailDueForVerification: false,
            isPrimaryEmailVerified: false,
          }
        : null),
      ...(formState[CMInfoSubtool.Email].isAlternateEmailVerified &&
      !failedSections?.includes("alternateEmail")
        ? {
            alternateEmailLastVerifiedDate: today,
            alternateEmailDueForVerification: false,
            isAlternateEmailVerified: false,
          }
        : null),
      ...(formState[CMInfoSubtool.Email].isEmailOptOutVerified &&
      !failedSections?.includes("emailOptOut")
        ? {
            emailOptOutLastVerifiedDate: today,
            emailOptOutDueForVerification: false,
            isEmailOptOutVerified: false,
          }
        : null),
    };
  }
  if (
    (subtoolToSubmit === undefined || subtoolToSubmit === CMInfoSubtool.P2D) &&
    formState[CMInfoSubtool.P2D].isPermissionToDiscussVerified
  ) {
    updatedFormState[CMInfoSubtool.P2D] = {
      ...updatedFormState[CMInfoSubtool.P2D],
      permissionToDiscussLastVerifiedDate: today,
      permissionToDiscussDueForVerification: false,
      isPermissionToDiscussVerified: false,
    };
  }
  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.EmploymentIncome) &&
    formState[CMInfoSubtool.EmploymentIncome].isIncomeVerified &&
    !failedSections?.includes("income")
  ) {
    updatedFormState[CMInfoSubtool.EmploymentIncome] = {
      ...updatedFormState[CMInfoSubtool.EmploymentIncome],
      incomeLastVerifiedDate: today,
      incomeDueForVerification: false,
      isIncomeVerified: false,
    };
  }
  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.School) &&
    formState[CMInfoSubtool.School].isSchoolVerified &&
    !failedSections?.includes("school") &&
    !failedSections?.includes("graduationDate")
  ) {
    updatedFormState[CMInfoSubtool.School] = {
      ...updatedFormState[CMInfoSubtool.School],
      schoolLastVerifiedDate: today,
      schoolDueForVerification: false,
      isSchoolVerified: false,
    };
  }
  if (
    (subtoolToSubmit === undefined ||
      subtoolToSubmit === CMInfoSubtool.ContactPreference) &&
    formState[CMInfoSubtool.ContactPreference].isContactPreferenceVerified &&
    !failedSections?.includes("contactPreference")
  ) {
    updatedFormState[CMInfoSubtool.ContactPreference] = {
      ...updatedFormState[CMInfoSubtool.ContactPreference],
      contactPreferenceLastVerifiedDate: today,
      contactPreferenceDueForVerification: false,
      isContactPreferenceVerified: false,
    };
  }
  if (
    subtoolToSubmit === undefined ||
    subtoolToSubmit === CMInfoSubtool.Security
  ) {
    updatedFormState[CMInfoSubtool.Security] = {
      ...updatedFormState[CMInfoSubtool.Security],
      ...(formState[CMInfoSubtool.Security].isDateOfBirthVerified
        ? {
            dateOfBirthLastVerifiedDate: today,
            dateOfBirthDueForVerification: false,
            isDateOfBirthVerified: false,
          }
        : null),
      ...(formState[CMInfoSubtool.Security].isMothersMaidenNameVerified
        ? {
            mothersMaidenNameLastVerifiedDate: today,
            mothersMaidenNameDueForVerification: false,
            isMothersMaidenNameVerified: false,
          }
        : null),
      ...(formState[CMInfoSubtool.Security].isSecurityVerified
        ? {
            securityLastVerifiedDate: today,
            securityDueForVerification: false,
            isSecurityVerified: false,
          }
        : null),
      ...(formState[CMInfoSubtool.Security].isSsnLast4Verified
        ? {
            ssnLast4LastVerifiedDate: today,
            ssnLast4DueForVerification: false,
            isSsnLast4Verified: false,
          }
        : null),
    };
  }

  return updatedFormState;
};

const getUpdatedInitialFormState = (
  updatedFormState: CMInfoFormInput,
  initialFormState: CMInfoFormInput,
  subtoolToSubmit: CMInfoSubtool | undefined,
  failedSections: CMInfoUpdateSectionType[] | null,
) => {
  const updatedInitialFormState =
    subtoolToSubmit === undefined
      ? { ...updatedFormState }
      : {
          ...initialFormState,
          [subtoolToSubmit]: updatedFormState[subtoolToSubmit],
        };

  if (failedSections?.includes("language")) {
    updatedInitialFormState[CMInfoSubtool.Language] =
      initialFormState[CMInfoSubtool.Language];
  }
  if (failedSections?.includes("billingAddress")) {
    updatedInitialFormState[CMInfoSubtool.BillingAddress] =
      initialFormState[CMInfoSubtool.BillingAddress];
  }
  if (failedSections?.includes("primaryEmail")) {
    updatedInitialFormState[CMInfoSubtool.Email] = {
      ...updatedInitialFormState[CMInfoSubtool.Email],
      primaryEmail: initialFormState[CMInfoSubtool.Email].primaryEmail,
      isPrimaryEmailVerified:
        initialFormState[CMInfoSubtool.Email].isPrimaryEmailVerified,
    };
  }
  if (failedSections?.includes("alternateEmail")) {
    updatedInitialFormState[CMInfoSubtool.Email] = {
      ...updatedInitialFormState[CMInfoSubtool.Email],
      alternateEmail: initialFormState[CMInfoSubtool.Email].alternateEmail,
      isAlternateEmailVerified:
        initialFormState[CMInfoSubtool.Email].isAlternateEmailVerified,
    };
  }
  if (failedSections?.includes("emailOptOut")) {
    updatedInitialFormState[CMInfoSubtool.Email] = {
      ...updatedInitialFormState[CMInfoSubtool.Email],
      emailOptOut: initialFormState[CMInfoSubtool.Email].emailOptOut,
      isEmailOptOutVerified:
        initialFormState[CMInfoSubtool.Email].isEmailOptOutVerified,
    };
  }
  if (failedSections?.includes("income")) {
    updatedInitialFormState[CMInfoSubtool.EmploymentIncome] =
      initialFormState[CMInfoSubtool.EmploymentIncome];
  }
  if (failedSections?.includes("school")) {
    updatedInitialFormState[CMInfoSubtool.School] = {
      ...updatedInitialFormState[CMInfoSubtool.School],
      school: initialFormState[CMInfoSubtool.School].school,
      isSchoolVerified: initialFormState[CMInfoSubtool.School].isSchoolVerified,
    };
  }
  if (failedSections?.includes("graduationDate")) {
    updatedInitialFormState[CMInfoSubtool.School] = {
      ...updatedInitialFormState[CMInfoSubtool.School],
      graduationDate: initialFormState[CMInfoSubtool.School].graduationDate,
      graduationStatus: initialFormState[CMInfoSubtool.School].graduationStatus,
      isSchoolVerified: initialFormState[CMInfoSubtool.School].isSchoolVerified,
    };
  }
  if (failedSections?.includes("contactPreference")) {
    updatedInitialFormState[CMInfoSubtool.ContactPreference] =
      initialFormState[CMInfoSubtool.ContactPreference];
  }

  return updatedInitialFormState;
};

export const useCMInfoForm = (
  dwbuid: string,
  referenceId: string = "",
  channel: ChannelFull,
  customerInfo: CustomerInfo,
  onDisplayGeneralErrors: (err: unknown) => void,
  onVerifiedAddressDateChange: (value: string) => void,
): {
  formState: CMInfoFormInput;
  formDispatch: (updatedFormState: CMInfoFormInput) => void;
  validationMessages: ValidationErrors | undefined;
  currentlyUpdating: CMInfoSubtool | "ALL" | null;
  failedSections: CMInfoUpdateSectionType[];
  initialFormState: CMInfoFormInput;
  onSubmit: (subtoolToSubmit?: CMInfoSubtool) => void;
} => {
  const [initialFormState, setInitialFormState] = useState<CMInfoFormInput>(
    getInitialFormState(customerInfo),
  );
  const [formState, setFormState] = useState<CMInfoFormInput>(initialFormState);
  const [validationMessages, setValidationMessages] =
    useState<ValidationErrors>();
  const [currentlyUpdating, setCurrentlyUpdating] = useState<
    CMInfoSubtool | "ALL" | null
  >(null);
  const [failedSections, setFailedSections] = useState<
    CMInfoUpdateSectionType[]
  >([]);

  const updateCustomerInfoMutation = useUpdateCustomerInfo(dwbuid, referenceId);

  const clearValidationMessages = useCallback((keys: string[]) => {
    setValidationMessages((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = [];
      });

      return next;
    });
  }, []);

  const formDispatch = useCallback(
    (updatedFormState: CMInfoFormInput) => {
      clearValidationMessages(
        (Object.keys(updatedFormState) as Array<CMInfoSubtool>).flatMap(
          (subtool) =>
            (
              Object.keys(updatedFormState[subtool]) as Array<
                keyof CMInfoFormInput[CMInfoSubtool]
              >
            ).filter(
              (field) =>
                formState[subtool][field] !== updatedFormState[subtool][field],
            ),
        ),
      );

      setFormState(updatedFormState);
    },
    [clearValidationMessages, formState],
  );

  const onSubmit = useCallback(
    (subtoolToSubmit?: CMInfoSubtool) => {
      setCurrentlyUpdating(subtoolToSubmit ?? "ALL");

      // validate locally before submit
      try {
        const schemaToParse = subtoolToSubmit
          ? schemas[subtoolToSubmit]
          : cmInfoRequestSchema;

        schemaToParse.parse(
          subtoolToSubmit ? formState[subtoolToSubmit] : formState,
        );
      } catch (err) {
        setCurrentlyUpdating(null);
        if (err instanceof ZodError && err.errors.length > 0) {
          const valMessages: ValidationErrors = {};
          err.errors.forEach((vm) => {
            const fieldName = vm.path[subtoolToSubmit ? 0 : 1];
            if (!(fieldName in valMessages)) {
              valMessages[fieldName] = [vm.message];
            }
          });
          setValidationMessages(valMessages);
          return;
        }
      }

      setValidationMessages(undefined);

      const updateRequest = createUpdateRequest(
        subtoolToSubmit,
        formState,
        initialFormState,
        dwbuid,
        referenceId,
        channel,
      );

      updateCustomerInfoMutation
        .mutateAsync(updateRequest)
        .then((res) => {
          setFailedSections(res.failedSections ?? []);

          const updatedFormState = getUpdatedFormState(
            formState,
            subtoolToSubmit,
            res.failedSections,
          );
          if (
            res.updatedAddress &&
            (subtoolToSubmit === undefined ||
              subtoolToSubmit === CMInfoSubtool.BillingAddress)
          ) {
            updatedFormState[CMInfoSubtool.BillingAddress] = {
              ...updatedFormState[CMInfoSubtool.BillingAddress],
              ...res.updatedAddress,
            };
          }
          if (
            updatedFormState[CMInfoSubtool.BillingAddress]
              .billingAddressDueForVerification === false &&
            updatedFormState[CMInfoSubtool.BillingAddress]
              .billingAddressLastVerifiedDate
          ) {
            onVerifiedAddressDateChange(
              convertDateStringToMonthDayYearFormat(
                updatedFormState[CMInfoSubtool.BillingAddress]
                  .billingAddressLastVerifiedDate,
              ),
            );
          }

          formDispatch(updatedFormState);

          setInitialFormState(
            getUpdatedInitialFormState(
              updatedFormState,
              initialFormState,
              subtoolToSubmit,
              res.failedSections,
            ),
          );
        })
        .catch((err) => {
          setValidationErrorsAndDisplayGeneralErrors(
            err,
            setValidationMessages,
            onDisplayGeneralErrors,
          );
        })
        .finally(() => setCurrentlyUpdating(null));
    },
    [
      dwbuid,
      formDispatch,
      formState,
      initialFormState,
      onDisplayGeneralErrors,
      onVerifiedAddressDateChange,
      referenceId,
      channel,
      updateCustomerInfoMutation,
    ],
  );

  return {
    formState,
    formDispatch,
    validationMessages,
    currentlyUpdating,
    failedSections,
    initialFormState,
    onSubmit,
  };
};
